import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Alert,
  Box,
  Button,
  Card,
  Center,
  Group,
  Image,
  PasswordInput,
  Progress,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { motion } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../api/client.js';
import { authApi } from '../api/endpoints.js';
import { brand } from '../theme/index.js';
import classes from './login.module.css';

export const Route = createFileRoute('/invite/$token')({
  component: AcceptInvitePage,
});

const MIN_LENGTH = 12;

/** Length is the property that actually resists an offline attack on argon2id. */
function strength(password: string): { value: number; label: string; color: string } {
  const length = password.length;
  if (length === 0) return { value: 0, label: '', color: 'gray' };
  if (length < MIN_LENGTH) return { value: 25, label: 'Too short', color: 'red' };
  if (length < 16) return { value: 60, label: 'Fine', color: 'yellow' };
  if (length < 24) return { value: 85, label: 'Good', color: 'teal' };
  return { value: 100, label: 'Excellent', color: 'teal' };
}

function AcceptInvitePage() {
  const { token } = Route.useParams();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: async () => {
      const result = await authApi.invite(token);
      setName(result.name);
      return result;
    },
    retry: false,
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authApi.acceptInvite(token, name.trim(), password);
      window.location.replace('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      setBusy(false);
    }
  };

  const meter = strength(password);

  return (
    <Box className={classes.page}>
      <Center h="100%" p="md">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: '100%', maxWidth: 420 }}
        >
          <Stack gap="lg">
            <Group justify="center">
              <Image src={brand.logo.mark} alt="" w={52} h={52} />
            </Group>

            {invite.isError ? (
              <Card withBorder radius="lg" p="lg" className={classes.card}>
                <Stack gap="sm" align="center">
                  <Title order={4}>This invite is no longer valid</Title>
                  <Text size="sm" c="dimmed" ta="center">
                    It may have expired, or already been used. Ask an admin to send a new one.
                  </Text>
                  <Button component="a" href="/login" variant="light" mt="sm">
                    Go to sign in
                  </Button>
                </Stack>
              </Card>
            ) : (
              <>
                <Stack gap={4} align="center">
                  <Title order={3}>Welcome to {brand.productName}</Title>
                  <Text size="sm" c="var(--rm-text-muted)">
                    Choose a password and you are in.
                  </Text>
                </Stack>

                <Card withBorder radius="lg" p="lg" className={classes.card}>
                  <form onSubmit={submit}>
                    <Stack gap="md">
                      {error && (
                        <Alert color="red" variant="light" radius="md" role="alert">
                          {error}
                        </Alert>
                      )}

                      <TextInput
                        label="Email"
                        value={invite.data?.email ?? ''}
                        readOnly
                        disabled
                      />

                      <TextInput
                        label="Your name"
                        value={name}
                        onChange={(event) => setName(event.currentTarget.value)}
                        required
                        autoComplete="name"
                      />

                      <Box>
                        <PasswordInput
                          label="Choose a password"
                          description={`At least ${MIN_LENGTH} characters. A memorable phrase beats a short scramble.`}
                          value={password}
                          onChange={(event) => setPassword(event.currentTarget.value)}
                          required
                          autoComplete="new-password"
                        />
                        {password.length > 0 && (
                          <Group gap="xs" mt={6} wrap="nowrap">
                            <Progress
                              value={meter.value}
                              color={meter.color}
                              size="xs"
                              style={{ flex: 1 }}
                            />
                            <Text size="xs" c={meter.color === 'red' ? 'red' : 'dimmed'}>
                              {meter.label}
                            </Text>
                          </Group>
                        )}
                      </Box>

                      <Button
                        type="submit"
                        fullWidth
                        loading={busy}
                        disabled={password.length < MIN_LENGTH || !name.trim()}
                        mt="xs"
                      >
                        Create my account
                      </Button>
                    </Stack>
                  </form>
                </Card>
              </>
            )}
          </Stack>
        </motion.div>
      </Center>
    </Box>
  );
}
